import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, UploadCloud, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_PHOTOS = 20;

export default function SellCards() {
  const [settings, setSettings] = useState({});
  const [form, setForm] = useState({ name: "", email: "", card_details: "", website: "" });
  const [images, setImages] = useState([]);  // {url, storage_path, filename}
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    document.title = "Sell Us Your Cards | Card Lobster";
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const upd = (k, v) => setForm({ ...form, [k]: v });

  const handleUpload = async (files) => {
    if (!files || !files.length) return;
    if (images.length + files.length > MAX_PHOTOS) {
      return toast.error(`Max ${MAX_PHOTOS} photos per submission`);
    }
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        if (!file.type.startsWith("image/")) {
          toast.error(`${file.name}: not an image`); continue;
        }
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/uploads/public", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((cur) => [...cur, {
          // Store the relative path; build the display URL at render time so
          // historical thumbnails don't break if the API domain changes.
          path: r.data.url,  // "/api/files/..."
          filename: file.name,
        }]);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (path) => setImages((cur) => cur.filter((i) => i.path !== path));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.card_details.trim()) {
      return toast.error("Name, email, and card details are required");
    }
    setBusy(true);
    try {
      await api.post("/sell-cards/submit", {
        ...form,
        image_urls: images.map((i) => i.path),
      });
      setSent(true);
      toast.success("Submission received — we'll review and reach out");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-16" data-testid="sell-cards-page">
      <div className="max-w-3xl">
        <div className="text-label text-[var(--cl-primary)]">{settings.sell_cards_kicker || "We're Buying"}</div>
        <h1 className="font-display text-5xl sm:text-7xl mt-2">
          {settings.sell_cards_title || "Turn Your Cards Into Cash"}
        </h1>
        <p className="text-zinc-300 text-lg mt-6 leading-relaxed">
          {settings.sell_cards_body ||
            "Card Lobster is always buying. We are actively looking to acquire high-quality singles, graded slabs, and select collections across the NFL, NBA, and MLB."}
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8 mt-14">
        <section>
          <h2 className="font-display text-3xl">What We're Buying</h2>
          <ul className="mt-6 space-y-4 text-zinc-300 text-sm">
            <Bullet title="Monster Case Hits">Downtowns, Uptowns, Kabooms, Color Blasts, and Zebras.</Bullet>
            <Bullet title="Graded Slabs">Premium PSA and Beckett (BGS) slabs.</Bullet>
            <Bullet title="Flagship Parallels & Short Prints">Prizm, Donruss Optic, Mosaic, Absolute Football, Topps Chrome, Select.</Bullet>
            <Bullet title="Premium Hits">High-end autographed cards and patch relics.</Bullet>
          </ul>

          <h2 className="font-display text-3xl mt-12">The Process</h2>
          <ol className="mt-6 space-y-4 text-zinc-300 text-sm">
            <Step n="1" title="Submit Your Cards">
              Tell us what you have — player, year, set, and any serial numbering (e.g., /150, /699, /850).
            </Step>
            <Step n="2" title="Upload Photos">
              Attach clear photos of the front and back of the cards (and the slab if graded).
            </Step>
            <Step n="3" title="Get an Offer">
              If it's a fit for our current inventory, we'll reach out with a competitive cash offer or trade value.
            </Step>
          </ol>
        </section>

        <section id="submit">
          <div className="border border-white/10 rounded-lg bg-[var(--cl-surface)] p-8">
            {sent ? (
              <div className="text-center py-8" data-testid="sell-cards-success">
                <CheckCircle2 size={48} className="mx-auto text-[var(--cl-orange)]" />
                <h3 className="font-display text-3xl mt-4">Submission received</h3>
                <p className="text-zinc-400 text-sm mt-3">
                  Thanks for sending these over. We'll review the photos and details and get back to you within 3 business days.
                </p>
              </div>
            ) : (
              <>
                <h3 className="font-display text-3xl">Submit your cards for review</h3>
                <form onSubmit={submit} className="mt-6 space-y-4">
                  {/* Honeypot: hidden from humans, bots fill it and get silently dropped */}
                  <input type="text" name="website" tabIndex={-1} autoComplete="off"
                         data-testid="sc-honeypot"
                         value={form.website} onChange={(e) => upd("website", e.target.value)}
                         aria-hidden="true"
                         style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />
                  <Field label="Name *" value={form.name} onChange={(v) => upd("name", v)} testid="sc-name" />
                  <Field label="Email *" type="email" value={form.email} onChange={(v) => upd("email", v)} testid="sc-email" />
                  <div>
                    <label className="text-label text-zinc-500">Card Details *</label>
                    <textarea rows={6} value={form.card_details} onChange={(e) => upd("card_details", e.target.value)}
                              data-testid="sc-details"
                              placeholder={"e.g.\n2020 Panini Prizm Justin Herbert RC #325, /75\n2018 Topps Chrome Ronald Acuña Jr Auto, PSA 10"}
                              className="w-full mt-2 bg-black border border-white/10 rounded px-4 py-3 text-sm focus:outline-none focus:border-[var(--cl-primary)]" />
                  </div>

                  <div>
                    <label className="text-label text-zinc-500 flex items-center gap-2">
                      <span>Photos</span>
                      <span className="text-[10px] text-zinc-600 normal-case tracking-normal">
                        {images.length}/{MAX_PHOTOS} · Max 8MB each · JPEG/PNG/WebP
                      </span>
                    </label>
                    <div className="mt-2 flex flex-wrap gap-3">
                      {images.map((img) => (
                        <div key={img.path} className="relative w-24 h-24 rounded border border-white/10 overflow-hidden group"
                             data-testid={`sc-image-${images.indexOf(img)}`}>
                          <img src={`${BACKEND_URL}${img.path}`} className="w-full h-full object-cover" alt={img.filename} />
                          <button type="button" onClick={() => removeImage(img.path)}
                                  className="absolute top-1 right-1 bg-black/80 rounded p-1 opacity-100 md:opacity-0 md:group-hover:opacity-100"
                                  data-testid={`sc-remove-${images.indexOf(img)}`}
                                  aria-label={`Remove ${img.filename}`}>
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                      {images.length < MAX_PHOTOS && (
                        <label className="w-24 h-24 rounded border border-dashed border-white/20 flex items-center justify-center cursor-pointer hover:border-[var(--cl-primary)]"
                               data-testid="sc-upload-btn">
                          <input type="file" multiple accept="image/jpeg,image/png,image/webp" className="hidden"
                                 onChange={(e) => handleUpload(e.target.files)} />
                          {uploading
                            ? <span className="text-xs text-zinc-500">…</span>
                            : <UploadCloud size={22} className="text-zinc-400" />}
                        </label>
                      )}
                    </div>
                  </div>

                  <button disabled={busy || uploading} className="btn-primary w-full" data-testid="sc-submit">
                    {busy ? "Submitting…" : "Submit for Review"}
                  </button>
                </form>
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function Bullet({ title, children }) {
  return (
    <li className="flex gap-3">
      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[var(--cl-orange)] flex-shrink-0" />
      <div><span className="text-white font-medium">{title}:</span> {children}</div>
    </li>
  );
}

function Step({ n, title, children }) {
  return (
    <li className="flex gap-4">
      <span className="font-display text-3xl text-[var(--cl-orange)] leading-none flex-shrink-0 w-8">{n}</span>
      <div><span className="text-white font-medium">{title}.</span> {children}</div>
    </li>
  );
}

function Field({ label, type = "text", value, onChange, testid, placeholder }) {
  return (
    <div>
      <label className="text-label text-zinc-500">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
             data-testid={testid}
             className="w-full mt-2 bg-black border border-white/10 rounded px-4 py-3 text-sm focus:outline-none focus:border-[var(--cl-primary)]" />
    </div>
  );
}
